/* eslint-disable no-shadow */
/* eslint-disable @typescript-eslint/no-shadow */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  AppWindow,
  AppWindowMac,
  Monitor,
  MonitorX,
  VideoOff,
  MicOff,
  Mic,
  Video,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { updateSelectedDevices, setDisplaySurface } from '../../recorderSlice';
import type { DisplaySurface } from '../../recorderSlice';
import {
  fetchAvailableDevices,
  fetchDisplayMediaThunk,
  fetchUserMediaThunk,
  initializeUploadThunk,
  startRecordingThunk,
  createBackupRecordThunk,
  cancelThunk,
} from '../../thunk';
import type { RecorderState, AppDispatch } from '../../store';
import logger from '../../../lib/logger';
import trackRecorderError from '../../utils/errorTracking';
import MacAppPromotionBanner from '../../../components/common/MacAppPromotionBanner';
import CameraPreview from '../MediaPreview/Camera';
import MicIconWithAudioVisualizer from './MicIconWithAudioVisualizer';

const DeviceSelection = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const isSmallViewport =
    typeof window !== 'undefined' && window.innerWidth < 640;

  const truncateText = (text: string, maxLength: number = 30) => {
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };
  const {
    status: recorderStatus,
    selectedDevices,
    availableDevices,
    mediaStreams,
    settings,
    upload,
  } = useSelector((state: RecorderState) => state.recorder);
  const { status: userMediaStatus } = mediaStreams.userMedia;
  const { videoinput, audioinput, displaySurface } = selectedDevices;
  const { webCam, screen } = settings?.videoSettings || {};
  const { microphoneAudio } = settings?.audioSettings || {};
  const [showAudioWarning, setShowAudioWarning] = useState(false);
  const [showNoAudioAlert, setShowNoAudioAlert] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Helper to check if upload is already initialized
  const isUploadInitialized = () => {
    return upload.uploadId && upload.fileId;
  };

  const displaySurfaceOptions: Record<
    DisplaySurface,
    { label: string; icon: React.ReactNode }
  > = {
    browser: {
      label: t('deviceSelection.displaySurface.browser', 'Browser tab'),
      icon: <AppWindow className="h-5 w-5" />,
    },
    window: {
      label: t('deviceSelection.displaySurface.window', 'Window'),
      icon: <AppWindowMac className="h-5 w-5" />,
    },
    monitor: {
      label: t('deviceSelection.displaySurface.monitor', 'Entire Screen'),
      icon: <Monitor className="h-5 w-5" />,
    },
    none: {
      label: t('deviceSelection.displaySurface.none', "Don't share screen"),
      icon: <MonitorX className="h-5 w-5" />,
    },
  };

  const getDisplaySurfaceIcon = (surface: DisplaySurface) => {
    return (
      displaySurfaceOptions[surface]?.icon || <Monitor className="h-5 w-5" />
    );
  };

  useEffect(() => {
    const handleDeviceChange = () => {
      console.log('Device change detected');
      dispatch(
        fetchAvailableDevices({ camera: webCam, microphone: microphoneAudio }),
      );
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        dispatch(
          fetchAvailableDevices({
            camera: webCam,
            microphone: microphoneAudio,
          }),
        );
      }
    };

    // Initial fetch
    dispatch(
      fetchAvailableDevices({ camera: webCam, microphone: microphoneAudio }),
    );

    // Setup event listeners
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        handleDeviceChange,
      );
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch]);

  useEffect(() => {
    if (availableDevices.status === 'succeeded') {
      const fetchMedia = async () => {
        const result = await dispatch(
          fetchUserMediaThunk({
            video:
              videoinput.deviceId !== 'none'
                ? { deviceId: videoinput.deviceId }
                : false,
            audio:
              audioinput.deviceId !== 'none'
                ? { deviceId: audioinput.deviceId }
                : false,
          }),
        );

        // Check if audio is working on initial load
        if (audioinput.deviceId !== 'none') {
          const stream = result as unknown as {
            payload: {
              trackInfo: Pick<MediaStreamTrack, 'label' | 'id' | 'kind'>[];
            };
          };
          const hasAudioTrack = stream?.payload?.trackInfo?.some(
            (track) => track.kind === 'audio',
          );
          setShowNoAudioAlert(!hasAudioTrack);
        } else {
          setShowNoAudioAlert(false);
        }
      };

      fetchMedia();
    }
  }, [availableDevices]);

  useEffect(() => {
    setShowAudioWarning(displaySurface !== 'browser');
  }, [displaySurface]);

  // Load saved audio settings from localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('recorderSettings');
    if (savedSettings) {
      const parsedSettings = JSON.parse(savedSettings);
      const savedAudioInput = parsedSettings?.selectedDevices?.audioinput;

      if (savedAudioInput !== 'none') {
        // Check if the saved device still exists in available devices
        const deviceExists = availableDevices.audioinput.find(
          (device) => device.deviceId === savedAudioInput,
        );

        if (deviceExists) {
          // Use the saved device if it exists
          dispatch(updateSelectedDevices({ audioinput: savedAudioInput }));
        } else if (availableDevices.audioinput.length > 0) {
          // If saved device not found, use the first available device
          dispatch(
            updateSelectedDevices({
              audioinput: availableDevices.audioinput[0].deviceId,
            }),
          );
        } else {
          // If no devices available, set to none
          dispatch(updateSelectedDevices({ audioinput: 'none' }));
        }
      } else {
        dispatch(updateSelectedDevices({ audioinput: 'none' }));
      }
    }
  }, [availableDevices, dispatch, microphoneAudio]);

  // Helper function to update localStorage
  const updateRecorderSettings = (
    kind: 'audio' | 'video',
    deviceId: string,
  ) => {
    const currentSettings = JSON.parse(
      localStorage.getItem('recorderSettings') || '{}',
    );
    const updatedSettings = {
      ...currentSettings,
      selectedDevices: {
        ...currentSettings.selectedDevices,
        [kind === 'audio' ? 'audioinput' : 'videoinput']: deviceId,
      },
    };
    localStorage.setItem('recorderSettings', JSON.stringify(updatedSettings));
  };

  const getHandleDeviceChange =
    (kind: 'audio' | 'video') => async (deviceId: string) => {
      try {
        const result = await (dispatch(
          fetchUserMediaThunk({
            [kind]: deviceId === 'none' ? false : { deviceId },
          }),
        ) as unknown as Promise<{
          streamId: string;
          trackInfo: Pick<MediaStreamTrack, 'label' | 'id' | 'kind'>[];
        }>);

        if (kind === 'audio') {
          if (deviceId === 'none') {
            setShowNoAudioAlert(false);
          } else {
            const stream = result as unknown as {
              payload: {
                trackInfo: Pick<MediaStreamTrack, 'label' | 'id' | 'kind'>[];
              };
            };
            // Just check if there's any audio track, not the specific label
            const hasAudioTrack = stream?.payload?.trackInfo?.some(
              (track) => track.kind === 'audio',
            );
            setShowNoAudioAlert(!hasAudioTrack);
          }
        } else {
          setShowNoAudioAlert(false);
        }
        updateRecorderSettings(kind, deviceId);
      } catch (error) {
        console.error('Error changing device:', error);
        if (kind === 'audio' && deviceId !== 'none') {
          setShowNoAudioAlert(true);
        }
      }
    };

  // Helper function to update localStorage
  const onChangeDisplaySurface = (value: DisplaySurface) => {
    dispatch(setDisplaySurface(value));
    const currentSettings = JSON.parse(
      localStorage.getItem('recorderSettings') || '{}',
    );
    const updatedSettings = {
      ...currentSettings,
      selectedDevices: {
        ...currentSettings.selectedDevices,
        displaySurface: value,
      },
    };
    localStorage.setItem('recorderSettings', JSON.stringify(updatedSettings));
  };

  const handleStart = async () => {
    // Prevent multiple initialization attempts
    if (hasInitialized || isUploadInitialized() || recorderStatus !== 'ready') {
      return;
    }

    if (displaySurface !== 'none') {
      const fetchDisplayMediaResult = await dispatch(
        fetchDisplayMediaThunk({
          video: {
            // @ts-ignore
            displaySurface,
          },
          audio: true,
        }),
      );
      if (
        // @ts-ignore - TS doesn't know about the type of the action
        fetchDisplayMediaResult.type === fetchDisplayMediaThunk.rejected.type
      ) {
        logger.log('Error fetching display media');
        dispatch(cancelThunk());
        return;
      }
    }
    if (
      !(
        displaySurface === 'none' &&
        videoinput.deviceId === 'none' &&
        audioinput.deviceId === 'none'
      )
    ) {
      setHasInitialized(true);
      const initResult = await dispatch(initializeUploadThunk());
      if (
        // @ts-ignore - TS doesn't know about the type of the action
        initResult.type === initializeUploadThunk.rejected.type
      ) {
        logger.error('Upload initialization failed, not starting recording');
        setHasInitialized(false); // Reset flag on failure
        trackRecorderError({
          operation: 'initialization',
          errorType: 'InitializationError',
          errorMessage: 'Upload initialization failed in device selection',
          additionalContext: {
            source: 'device_selection',
            displaySurface,
            selectedVideoinput: videoinput.deviceId,
            selectedAudioinput: audioinput.deviceId,
          },
        });
        return;
      }

      await dispatch(startRecordingThunk());
      await dispatch(createBackupRecordThunk());
    }
  };

  // Add new useEffect for auto-starting when isTabAudioOnly is true
  useEffect(() => {
    if (
      settings?.audioSettings?.isTabAudioOnly &&
      recorderStatus === 'ready' &&
      availableDevices.status === 'succeeded' &&
      !hasInitialized &&
      !isUploadInitialized()
    ) {
      handleStart();
    }
  }, [
    settings?.audioSettings?.isTabAudioOnly,
    recorderStatus,
    availableDevices.status,
    hasInitialized,
  ]);

  // Reset initialization flag when recorder status changes back to initializing
  useEffect(() => {
    if (recorderStatus === 'initializing') {
      setHasInitialized(false);
    }
  }, [recorderStatus]);

  if (recorderStatus !== 'ready') {
    return null;
  }

  if (availableDevices.status === 'pending') {
    return <div className="text-center p-4">Loading devices...</div>;
  }

  if (availableDevices.status === 'failed') {
    return (
      <div className="text-center p-4 text-destructive">
        Error loading devices: {availableDevices.error}
      </div>
    );
  }

  if (settings?.audioSettings?.isTabAudioOnly) {
    return null;
  }

  return (
    <Dialog open onOpenChange={() => dispatch(cancelThunk())}>
      <DialogContent
        className={cn('max-w-md', isSmallViewport && 'w-full m-2')}
      >
        <DialogHeader>
          <DialogTitle className="text-center text-base">
            {t('deviceSelection.title', 'Choose what to record')}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="min-h-0">
            <MacAppPromotionBanner variant="recorder" />
          </div>

          {/* Preview Section - More compact */}
          <div className="space-y-2 -mt-3">
            {/* Video Preview - Only show if not audio-only mode */}
            {(screen || webCam) &&
              !(
                displaySurface === 'none' &&
                videoinput.deviceId === 'none' &&
                audioinput.deviceId !== 'none'
              ) && (
                <div className="relative rounded-lg overflow-hidden bg-muted aspect-[16/10] flex items-center justify-center">
                  {/* Render content based on what's selected */}
                  {(() => {
                    // Screen share placeholder when screen is selected
                    if (screen && displaySurface !== 'none') {
                      return (
                        <div className="w-full h-full bg-secondary/20 flex items-center justify-center">
                          <div className="text-center space-y-3">
                            <div className="flex justify-center">
                              {React.cloneElement(
                                getDisplaySurfaceIcon(
                                  displaySurface,
                                ) as React.ReactElement,
                                {
                                  className: 'h-16 w-16 text-gray-400',
                                },
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {displaySurfaceOptions[displaySurface]?.label}
                            </p>
                          </div>
                        </div>
                      );
                    }

                    // Full camera preview when only camera is selected
                    if (webCam && videoinput.deviceId !== 'none') {
                      return (
                        <div className="w-full h-full">
                          <CameraPreview fullSize />
                        </div>
                      );
                    }

                    // Empty state - show "Audio Only" if microphone is enabled, otherwise "No video source selected"
                    return (
                      <div className="text-center">
                        <div className="flex flex-col items-center gap-2">
                          {audioinput.deviceId !== 'none' &&
                          audioinput.deviceId ? (
                            <>
                              <div className="relative">
                                <MicIconWithAudioVisualizer
                                  deviceId={audioinput.deviceId}
                                  className="h-9 w-9 text-gray-400"
                                />
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Audio Only
                              </p>
                            </>
                          ) : (
                            <>
                              <VideoOff className="h-10 w-10 text-gray-400" />
                              <p className="text-xs text-muted-foreground">
                                No video source selected
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Camera PiP when screen share is active and camera is on */}
                  {screen &&
                    displaySurface !== 'none' &&
                    webCam &&
                    videoinput.deviceId !== 'none' && (
                      <div className="absolute bottom-2 left-2 w-24 h-24 z-10">
                        <CameraPreview />
                      </div>
                    )}
                </div>
              )}
          </div>

          {/* Compact device selectors */}
          <div className="flex flex-col gap-2">
            {/* Display Surface Selector */}
            {screen && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-muted-foreground min-w-[70px]">
                  {t('deviceSelection.screenLabel', 'Screen')}:
                </span>
                <Select
                  value={displaySurface}
                  onValueChange={(value) =>
                    onChangeDisplaySurface(value as DisplaySurface)
                  }
                >
                  <SelectTrigger className="w-full h-9 border bg-background px-2.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="flex-shrink-0">
                        {getDisplaySurfaceIcon(displaySurface)}
                      </div>
                      <span className="text-sm truncate">
                        {displaySurfaceOptions[displaySurface]?.label}
                      </span>
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(displaySurfaceOptions).map(
                      ([key, { label, icon }]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="flex-shrink-0">{icon}</div>
                            <span className="truncate">{label}</span>
                          </div>
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Microphone Selector */}
            {microphoneAudio && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-muted-foreground min-w-[70px]">
                  {t('deviceSelection.microphoneLabel', 'Mic')}:
                </span>
                <Select
                  value={audioinput.deviceId || 'none'}
                  onValueChange={(value) => {
                    getHandleDeviceChange('audio')(value);
                    dispatch(updateSelectedDevices({ audioinput: value }));
                  }}
                >
                  <SelectTrigger className="w-full h-9 border bg-background px-2.5">
                    <div className="flex items-center gap-2 min-w-0">
                      {audioinput.deviceId === 'none' ||
                      !audioinput.deviceId ? (
                        <>
                          <MicOff className="h-5 w-5 text-destructive flex-shrink-0" />
                          <span className="text-sm text-destructive truncate">
                            {t('deviceSelection.microphone.off', 'Off')}
                          </span>
                        </>
                      ) : (
                        <>
                          <MicIconWithAudioVisualizer
                            deviceId={audioinput.deviceId}
                            className="h-5 w-5"
                          />
                          <span className="text-sm -ml-2 truncate">
                            {truncateText(
                              availableDevices.audioinput.find(
                                (d) => d.deviceId === audioinput.deviceId,
                              )?.label ||
                                t(
                                  'deviceSelection.microphone.default',
                                  'Microphone',
                                ),
                            )}
                          </span>
                        </>
                      )}
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <div className="flex items-center gap-2 min-w-0">
                        <MicOff className="h-5 w-5 text-destructive flex-shrink-0" />
                        <span className="text-destructive truncate">
                          {t(
                            'deviceSelection.microphone.dontUse',
                            "Don't use microphone",
                          )}
                        </span>
                      </div>
                    </SelectItem>
                    {availableDevices.audioinput.map((device) => (
                      <SelectItem key={device.deviceId} value={device.deviceId}>
                        <div className="flex items-center gap-2 min-w-0">
                          <Mic className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                          <span className="truncate">
                            {truncateText(
                              device.label || `Microphone ${device.deviceId}`,
                            )}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Camera Selector */}
            {webCam && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-muted-foreground min-w-[70px]">
                  {t('deviceSelection.cameraLabel', 'Camera')}:
                </span>
                <Select
                  value={videoinput.deviceId || 'none'}
                  onValueChange={(value) => {
                    getHandleDeviceChange('video')(value);
                    dispatch(updateSelectedDevices({ videoinput: value }));
                  }}
                >
                  <SelectTrigger className="w-full h-9 border bg-background px-2.5">
                    <div className="flex items-center gap-2 min-w-0">
                      {videoinput.deviceId === 'none' ||
                      !videoinput.deviceId ? (
                        <>
                          <VideoOff className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm truncate">
                            {t('deviceSelection.camera.off', 'Off')}
                          </span>
                        </>
                      ) : (
                        <>
                          <Video className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm truncate">
                            {truncateText(
                              availableDevices.videoinput.find(
                                (d) => d.deviceId === videoinput.deviceId,
                              )?.label ||
                                t('deviceSelection.camera.default', 'Camera'),
                            )}
                          </span>
                        </>
                      )}
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <div className="flex items-center gap-2 min-w-0">
                        <VideoOff className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                        <span className="truncate">
                          {t(
                            'deviceSelection.camera.dontUse',
                            "Don't use camera",
                          )}
                        </span>
                      </div>
                    </SelectItem>
                    {availableDevices.videoinput.map((device) => (
                      <SelectItem key={device.deviceId} value={device.deviceId}>
                        <div className="flex items-center gap-2 min-w-0">
                          <Video className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                          <span className="truncate">
                            {truncateText(
                              device.label || `Camera ${device.deviceId}`,
                            )}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Alerts with smooth animation */}
          <div className="min-h-0">
            {/* Warning Alert */}
            <div
              className="overflow-hidden transition-all duration-300 ease-in-out"
              style={{
                maxHeight:
                  (showAudioWarning && screen) ||
                  (audioinput.deviceId === 'none' && microphoneAudio)
                    ? '200px'
                    : '0px',
                opacity:
                  (showAudioWarning && screen) ||
                  (audioinput.deviceId === 'none' && microphoneAudio)
                    ? 1
                    : 0,
                marginBottom:
                  (showAudioWarning && screen) ||
                  (audioinput.deviceId === 'none' && microphoneAudio)
                    ? '8px'
                    : '0px',
              }}
            >
              <Alert className="bg-muted border-muted">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <AlertDescription className="text-muted-foreground text-sm">
                    {(() => {
                      if (
                        showAudioWarning &&
                        screen &&
                        audioinput.deviceId === 'none' &&
                        microphoneAudio
                      ) {
                        return t(
                          'deviceSelection.microphoneOffAndBrowserAudio',
                          'Microphone is off. Browser audio will not be captured. Share a browser tab to record browser audio.',
                        );
                      }
                      if (showAudioWarning && screen) {
                        return t(
                          'deviceSelection.audioWarning',
                          'Browser audio will not be captured. Share a browser tab to record browser audio.',
                        );
                      }
                      return t(
                        'deviceSelection.microphoneOff',
                        'Microphone is off',
                      );
                    })()}
                  </AlertDescription>
                </div>
              </Alert>
            </div>

            {/* Error Alert */}
            <div
              className="overflow-hidden transition-all duration-300 ease-in-out"
              style={{
                maxHeight:
                  showNoAudioAlert && audioinput.deviceId !== 'none'
                    ? '200px'
                    : '0px',
                opacity:
                  showNoAudioAlert && audioinput.deviceId !== 'none' ? 1 : 0,
                marginBottom:
                  showNoAudioAlert && audioinput.deviceId !== 'none'
                    ? '8px'
                    : '0px',
              }}
            >
              <Alert variant="destructive">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <AlertDescription className="text-sm">
                    {t(
                      'deviceSelection.noAudioAlert',
                      'No audio microphone audio detected. Please select "Don\'t use microphone Audio" if you want to proceed.',
                    )}
                  </AlertDescription>
                </div>
              </Alert>
            </div>
          </div>

          <Button
            id="start-recording-button"
            onClick={handleStart}
            disabled={
              userMediaStatus === 'pending' ||
              (displaySurface === 'none' &&
                videoinput.deviceId === 'none' &&
                audioinput.deviceId === 'none')
            }
            size="default"
            className="w-full h-9 text-sm"
          >
            {t('deviceSelection.startRecording', 'Start Recording')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeviceSelection;
